/**
 * Strategic context for cycle setup — link a classic strategy or an
 * opportunity solution tree.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import {
  BarChart3,
  ExternalLink,
  Loader2,
  Plus,
  RefreshCw,
  TreeDeciduous,
} from 'lucide-react';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useStrategyStore } from '@/stores/prioritization/strategyStore';
import { useOSTStore } from '@/stores/prioritization/ostStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { useWorkspaceModules, isModuleEnabled } from '@/lib/workspaceModulesService';
import { useUIStore } from '@/stores/prioritization/uiStore';
import { FEATURE_FLAGS, isFeatureEnabled } from '@/lib/featureFlags';
import {
  SETUP_OST_TREE_ID_KEY,
  SETUP_OST_TREE_NAME_KEY,
  getSetupOstTreeId,
} from '@/lib/cycleSetupPhase';
import type { StrategyRef } from '@/types/prioritization/degapCycle';
import {
  WORKSPACE_STRATEGY_KIND_LABELS,
  workspaceStrategyHref,
} from '@/lib/strategy/workspaceStrategies';
import {
  wizardBtnPrimary,
  wizardBtnSecondary,
  wizardCardPad,
  wizardInput,
  wizardSectionDesc,
  wizardSectionTitle,
  wizardSelect,
} from './setupWizardTheme';

type AnchorMode = 'strategy' | 'ost' | null;

interface CycleStrategicContextSectionProps {
  cycleId: string;
  cycleName: string;
}

function OptionCard({
  active,
  onSelect,
  icon,
  title,
  description,
  accentClass,
  children,
}: {
  active: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
  accentClass: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={` border p-4 sm:p-5 transition-colors ${
        active
          ? 'border-primary-400 dark:border-primary-600/60 bg-primary-50/50 dark:bg-primary-950/20 ring-1 ring-primary-200/80 dark:ring-primary-800/40'
          : 'border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)]'
      }`}
    >
      <button
        type="button"
        onClick={onSelect}
        className="w-full text-left"
      >
        <div className="flex items-start gap-3">
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center  ${accentClass}`}
          >
            {icon}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {title}
            </p>
            <p className="mt-0.5 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
              {description}
            </p>
          </div>
          <div
            className={`mt-1 h-4 w-4 shrink-0 rounded-full border-2 ${
              active
                ? 'border-primary-600 bg-primary-600 dark:border-primary-500 dark:bg-primary-500'
                : 'border-[var(--rule)] dark:border-[var(--rule-bright)]'
            }`}
            aria-hidden
          />
        </div>
      </button>
      {active && <div className="mt-4 pt-4 border-t border-[var(--rule)]/80 dark:border-[var(--rule)]">{children}</div>}
    </div>
  );
}

export function CycleStrategicContextSection({
  cycleId,
  cycleName,
}: CycleStrategicContextSectionProps) {
  const cycle = useDegapProcessStore((s) => s.cycles.find((c) => c.id === cycleId));
  const updateCycle = useDegapProcessStore((s) => s.updateCycle);

  const userId = useUserStore((s) => s.userId);
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId) ?? cycle?.workspaceId ?? '';
  const addNotification = useUIStore((s) => s.addNotification);

  const strategies = useStrategyStore(useShallow((s) => Object.values(s.strategies)));
  const fetchStrategies = useStrategyStore((s) => s.fetchFromSupabase);
  const createStrategy = useStrategyStore((s) => s.createStrategy);
  const linkCycle = useStrategyStore((s) => s.linkCycle);

  const ostTrees = useOSTStore((s) => s.trees);
  const fetchTrees = useOSTStore((s) => s.fetchTrees);
  const createTree = useOSTStore((s) => s.createTree);
  const fetchNodes = useOSTStore((s) => s.fetchNodes);

  const workspaceFeatures = useWorkspaceModules();
  const strategyModuleOn = useMemo(
    () =>
      (isModuleEnabled(workspaceFeatures, 'strategy') &&
        isFeatureEnabled(FEATURE_FLAGS.STRATEGY_NAV)) ||
      (isModuleEnabled(workspaceFeatures, 'strategy_kernel') &&
        isFeatureEnabled(FEATURE_FLAGS.STRATEGY_KERNEL_NAV)),
    [workspaceFeatures],
  );

  const linkedStrategyId = cycle?.strategyRef?.id ?? '';
  const linkedOstId = getSetupOstTreeId(cycle);
  const linkedOst = ostTrees.find((t) => t.id === linkedOstId) ?? null;
  const linkedStrategy = strategies.find((s) => s.id === linkedStrategyId) ?? null;
  // A cycle can be linked to a strategy of any kind (the create form offers all
  // five). One that is not a classic strategy is shown by what the link itself
  // records: its name, its kind, and where it opens.
  const linkedOtherKind =
    cycle?.strategyRef?.id && cycle.strategyRef.kind && cycle.strategyRef.kind !== 'classic'
      ? cycle.strategyRef.kind
      : null;

  const [anchorMode, setAnchorMode] = useState<AnchorMode>(() => {
    if (linkedOstId) return 'ost';
    if (linkedStrategyId || cycle?.strategyRef?.name) return 'strategy';
    return null;
  });

  const [strategyPickId, setStrategyPickId] = useState(linkedStrategyId);
  const [newStrategyName, setNewStrategyName] = useState('');
  const [creatingStrategy, setCreatingStrategy] = useState(false);

  const [newOstName, setNewOstName] = useState('');
  const [ostPickId, setOstPickId] = useState(linkedOstId ?? '');
  const [creatingOst, setCreatingOst] = useState(false);
  const [saving, setSaving] = useState(false);

  const ostNodeCount = useOSTStore((s) =>
    linkedOstId ? s.nodes.filter((n) => n.treeId === linkedOstId).length : 0,
  );

  useEffect(() => {
    if (strategyModuleOn) void fetchStrategies();
    void fetchTrees(workspaceId || undefined);
  }, [strategyModuleOn, fetchStrategies, fetchTrees, workspaceId]);

  useEffect(() => {
    if (linkedOstId) void fetchNodes(linkedOstId);
  }, [linkedOstId, fetchNodes]);

  useEffect(() => {
    setStrategyPickId(linkedStrategyId);
    setOstPickId(linkedOstId ?? '');
    if (linkedOstId) setAnchorMode('ost');
    else if (linkedStrategyId || cycle?.strategyRef?.name) setAnchorMode('strategy');
  }, [linkedStrategyId, linkedOstId, cycle?.strategyRef?.name]);

  const persistStrategyRef = useCallback(
    async (ref: StrategyRef | undefined, strategyIdToLink?: string) => {
      if (!cycle) return;
      setSaving(true);
      try {
        const existingMeta =
          cycle.metadata && typeof cycle.metadata === 'object'
            ? { ...(cycle.metadata as Record<string, unknown>) }
            : {};
        const { [SETUP_OST_TREE_ID_KEY]: _a, [SETUP_OST_TREE_NAME_KEY]: _b, ...rest } = existingMeta;

        await updateCycle(cycleId, {
          strategyRef: ref,
          metadata: rest,
        });

        if (strategyIdToLink) {
          linkCycle(strategyIdToLink, cycleId);
        }
      } finally {
        setSaving(false);
      }
    },
    [cycle, cycleId, updateCycle, linkCycle],
  );

  const persistOstLink = useCallback(
    async (treeId: string, treeName: string) => {
      if (!cycle) return;
      setSaving(true);
      try {
        const existingMeta =
          cycle.metadata && typeof cycle.metadata === 'object'
            ? { ...(cycle.metadata as Record<string, unknown>) }
            : {};
        await updateCycle(cycleId, {
          strategyRef: undefined,
          metadata: {
            ...existingMeta,
            [SETUP_OST_TREE_ID_KEY]: treeId,
            [SETUP_OST_TREE_NAME_KEY]: treeName,
          },
        });
      } finally {
        setSaving(false);
      }
    },
    [cycle, cycleId, updateCycle],
  );

  const handleLinkStrategy = async (strategyId: string) => {
    const found = strategies.find((s) => s.id === strategyId);
    if (!found) return;
    setStrategyPickId(strategyId);
    setAnchorMode('strategy');
    await persistStrategyRef({ id: found.id, name: found.name, source: 'internal' }, found.id);
  };

  const handleCreateStrategy = async () => {
    const name = newStrategyName.trim() || `${cycleName} Strategy`;
    if (!userId) return;
    setCreatingStrategy(true);
    try {
      const created = await createStrategy({
        name,
        ownerId: userId,
        timeHorizon: 'annual',
      });
      if (!created.ok || !created.id) {
        // Keep whatever the user typed so they can retry without re-entering it.
        addNotification({
          type: 'error',
          title: 'Strategy was not created',
          message: created.error ?? 'Try again.',
          duration: 6000,
        });
        return;
      }
      setNewStrategyName('');
      await handleLinkStrategy(created.id);
    } finally {
      setCreatingStrategy(false);
    }
  };

  const handleLinkOst = async (treeId: string) => {
    const tree = ostTrees.find((t) => t.id === treeId);
    if (!tree) return;
    setOstPickId(treeId);
    setAnchorMode('ost');
    await persistOstLink(tree.id, tree.name);
  };

  const handleCreateOst = async () => {
    const name = newOstName.trim() || `${cycleName} OST`;
    setCreatingOst(true);
    try {
      const tree = await createTree({
        name,
        description: `Opportunity solution tree for ${cycleName}`,
        ownerId: userId ?? undefined,
        workspaceId: workspaceId || undefined,
      });
      if (!tree) return;
      setNewOstName('');
      await handleLinkOst(tree.id);
    } finally {
      setCreatingOst(false);
    }
  };

  const handleClearAnchor = async () => {
    if (!cycle) return;
    setAnchorMode(null);
    setStrategyPickId('');
    setOstPickId('');
    setSaving(true);
    try {
      const existingMeta =
        cycle.metadata && typeof cycle.metadata === 'object'
          ? { ...(cycle.metadata as Record<string, unknown>) }
          : {};
      const { [SETUP_OST_TREE_ID_KEY]: _a, [SETUP_OST_TREE_NAME_KEY]: _b, ...rest } = existingMeta;
      await updateCycle(cycleId, { strategyRef: undefined, metadata: rest });
    } finally {
      setSaving(false);
    }
  };

  if (!strategyModuleOn) {
    return (
      <div className={wizardCardPad}>
        <h3 className={wizardSectionTitle}>Strategic context</h3>
        <p className={wizardSectionDesc}>
          Enable Strategy in workspace settings to link a strategy or opportunity solution tree.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={wizardCardPad}>
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h3 className={wizardSectionTitle}>Strategic context</h3>
            <p className={wizardSectionDesc}>
              Anchor this cycle to a classic strategy or an opportunity solution tree.
            </p>
          </div>
          {(linkedStrategy || linkedOst || linkedOtherKind) && (
            <button
              type="button"
              onClick={() => void handleClearAnchor()}
              disabled={saving}
              className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)]"
            >
              Clear link
            </button>
          )}
        </div>

        {linkedOtherKind && cycle?.strategyRef?.id ? (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-3">
            <div className="min-w-0">
              <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--text-ter)]">
                Linked strategy · {WORKSPACE_STRATEGY_KIND_LABELS[linkedOtherKind]}
              </p>
              <p className="mt-0.5 text-sm font-medium text-[var(--text-pri)]">{cycle.strategyRef.name}</p>
            </div>
            <Link
              to={workspaceStrategyHref(linkedOtherKind, cycle.strategyRef.id)}
              className="inline-flex items-center gap-1 text-xs font-medium text-[var(--text-pri)] underline-offset-2 hover:underline"
            >
              Open
              <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
        ) : null}

        <div className="grid gap-4 md:grid-cols-2">
          <OptionCard
            active={anchorMode === 'strategy'}
            onSelect={() => setAnchorMode('strategy')}
            icon={<BarChart3 className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />}
            title="Classic strategy"
            description="Vision-driven alignment — link an existing strategy or create one."
            accentClass="bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40"
          >
            {linkedStrategy ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                    {linkedStrategy.name}
                  </p>
                  <Link
                    to={`/prioritization/strategy/${linkedStrategy.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Open
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
                <button
                  type="button"
                  onClick={() => setStrategyPickId('')}
                  className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline"
                >
                  Change strategy
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {strategies.length > 0 && (
                  <div className="flex gap-2">
                    <select
                      value={strategyPickId}
                      onChange={(e) => setStrategyPickId(e.target.value)}
                      className={wizardSelect}
                    >
                      <option value="">Choose an existing strategy…</option>
                      {strategies.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => strategyPickId && void handleLinkStrategy(strategyPickId)}
                      disabled={!strategyPickId || saving}
                      className={wizardBtnSecondary}
                    >
                      Link
                    </button>
                  </div>
                )}
                <div className="border border-dashed border-[var(--rule)] dark:border-[var(--rule-bright)] p-3">
                  <p className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2">
                    Or create a new strategy
                  </p>
                  <div className="flex gap-2">
                    <input
                      value={newStrategyName}
                      onChange={(e) => setNewStrategyName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleCreateStrategy();
                        }
                      }}
                      placeholder={`${cycleName} Strategy`}
                      className={wizardInput}
                    />
                    <button
                      type="button"
                      onClick={() => void handleCreateStrategy()}
                      disabled={creatingStrategy || saving}
                      className={wizardBtnPrimary}
                    >
                      {creatingStrategy ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      Create
                    </button>
                  </div>
                </div>
              </div>
            )}
          </OptionCard>

          <OptionCard
            active={anchorMode === 'ost'}
            onSelect={() => setAnchorMode('ost')}
            icon={<TreeDeciduous className="h-4 w-4 text-primary-600 dark:text-primary-400" />}
            title="Opportunity solution tree"
            description="Continuous discovery — Teresa Torres-style outcome-first framing."
            accentClass="bg-primary-100 dark:bg-primary-950/40"
          >
            {linkedOst ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                      {linkedOst.name}
                    </p>
                    <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                      {ostNodeCount === 0
                        ? 'Tree is empty — add nodes in the editor.'
                        : `${ostNodeCount} node${ostNodeCount === 1 ? '' : 's'} in tree`}
                    </p>
                  </div>
                  <Link
                    to={`/prioritization/strategy/opportunity-tree/${linkedOst.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Open tree
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
                <button
                  type="button"
                  onClick={() => setOstPickId('')}
                  className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline"
                >
                  Change tree
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="border border-dashed border-[var(--rule)] dark:border-[var(--rule-bright)] p-3">
                  <p className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2">
                    Create a new tree for this cycle
                  </p>
                  <div className="flex gap-2">
                    <input
                      value={newOstName}
                      onChange={(e) => setNewOstName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleCreateOst();
                        }
                      }}
                      placeholder={`${cycleName} OST`}
                      className={wizardInput}
                    />
                    <button
                      type="button"
                      onClick={() => void handleCreateOst()}
                      disabled={creatingOst || saving}
                      className={wizardBtnPrimary}
                    >
                      {creatingOst ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                      Create
                    </button>
                  </div>
                </div>

                {ostTrees.length > 0 && (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 border-t border-[var(--rule)] dark:border-[var(--rule)]" />
                      <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                        or link existing
                      </span>
                      <div className="flex-1 border-t border-[var(--rule)] dark:border-[var(--rule)]" />
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={ostPickId}
                        onChange={(e) => setOstPickId(e.target.value)}
                        className={wizardSelect}
                      >
                        <option value="">Choose a tree…</option>
                        {ostTrees.map((tree) => (
                          <option key={tree.id} value={tree.id}>
                            {tree.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => ostPickId && void handleLinkOst(ostPickId)}
                        disabled={!ostPickId || saving}
                        className={wizardBtnSecondary}
                      >
                        Link
                      </button>
                      <button
                        type="button"
                        onClick={() => void fetchTrees(workspaceId || undefined)}
                        title="Refresh list"
                        className={wizardBtnSecondary}
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </OptionCard>
        </div>

        {saving && (
          <p className="mt-3 flex items-center gap-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            <Loader2 className="h-3 w-3 animate-spin" />
            Saving…
          </p>
        )}
      </div>
    </div>
  );
}
